import type { ReactNode } from 'react';
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  type MenuItemProps as AriaMenuItemProps,
  type MenuProps as AriaMenuProps,
  MenuTrigger,
  Popover,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

export interface MenuProps<T> extends AriaMenuProps<T> {
  /** The element that opens the menu (usually a Button or IconButton). */
  trigger: ReactNode;
  placement?: 'bottom start' | 'bottom end';
}

/** Dropdown menu of actions, opened from a trigger. Keyboard and screen-reader behavior from React Aria. */
export function Menu<T extends object>({ trigger, placement = 'bottom end', ...props }: MenuProps<T>) {
  return (
    <MenuTrigger>
      {trigger}
      <Popover
        placement={placement}
        className="min-w-48 rounded-md border border-border bg-raised p-1 shadow-lg outline-none"
      >
        <AriaMenu {...props} className="outline-none" />
      </Popover>
    </MenuTrigger>
  );
}

const itemStyles = tv({
  base: 'flex min-h-10 cursor-default items-center gap-2 rounded-sm px-3 text-sm outline-none data-[focused]:bg-sunken data-[disabled]:opacity-50',
  variants: { tone: { default: 'text-fg', danger: 'text-danger-fg' } },
  defaultVariants: { tone: 'default' },
});

export interface MenuItemProps extends Omit<AriaMenuItemProps, 'className'> {
  tone?: 'default' | 'danger';
  children: ReactNode;
}

export function MenuItem({ tone, children, ...props }: MenuItemProps) {
  return (
    <AriaMenuItem {...props} className={itemStyles({ tone })}>
      {children}
    </AriaMenuItem>
  );
}
